import React, { useEffect, useState, useMemo } from 'react';
import { UserCog, Plus, Search, Eye, Edit, Trash2, Mail, Phone, Star, MoreVertical, Send, SlidersHorizontal } from 'lucide-react';
import InstructorCard from './InstructorCard';
import { PageHeader, AppButton, ViewToggle, StatusBadge, UserAvatar, EmptyState, AppCard, AppCardContent, FilterModal, FilterGroup, ConfirmModal } from '../ui';
import apiClient from '../../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../../contexts/BranchContext';
import './Instructors.scss';
import CommonToaster from '../Common/CommonToaster';
import OverLimitBanner from '../Common/OverLimitBanner';
import { usePlanUsage, isOverLimit, isStrictlyOverLimit } from '../../hooks/usePlanUsage';

const InstructorsPage = () => {
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();

  const activeFilterCount = [filters.status].filter(Boolean).length;

  const handleResetFilters = () => {
    setFilters((f) => ({ ...f, status: '' }));
    setShowFilterModal(false);
  };

  // Plan usage limits
  const { usage } = usePlanUsage();
  const instructorLimit = isOverLimit(usage?.instructors);
  const instructorStrictly = isStrictlyOverLimit(usage?.instructors);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const { data } = await apiClient.get('/instructors/list', { params: filters });
        setInstructors(data.instructors || []);
      } catch {
        setToast({ show: true, message: 'Failed to load instructors.', type: 'error' });
      }
      setLoading(false);
    }
    fetchData();
  }, [filters, selectedBranchId]);

  const filteredInstructors = useMemo(() => {
    let list = instructors;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(i =>
        i.name?.toLowerCase().includes(q) ||
        i.email?.toLowerCase().includes(q) ||
        i.subject?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [instructors, filters.search]);

  const [confirmAction, setConfirmAction] = useState(null);

  const handleDelete = (inst) => {
    setConfirmAction({
      title: 'Delete Instructor',
      message: `Are you sure you want to delete instructor "${inst.name}"?`,
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await apiClient.delete(`/instructors/${inst._id}`);
          setInstructors(prev => prev.filter(i => i._id !== inst._id));
          setToast({ show: true, message: 'Instructor deleted.', type: 'success' });
        } catch {
          setToast({ show: true, message: 'Failed to delete instructor.', type: 'error' });
        }
      },
    });
  };

  const resendWelcomeEmail = (inst) => {
    setConfirmAction({
      title: 'Resend Welcome Email',
      message: `Are you sure you want to resend welcome email to "${inst.name}"?`,
      variant: 'info',
      confirmText: 'Resend',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await apiClient.post(`/instructors/${inst._id}/resend-welcome-email`);
          setToast({ show: true, message: 'Welcome email resent successfully.', type: 'success' });
        } catch {
          setToast({ show: true, message: 'Failed to resend welcome email.', type: 'error' });
        }
      },
    });
  };

  return (
    <div className="instructors-page">
      <OverLimitBanner
        resourceName="Instructors"
        used={instructorLimit.used}
        max={instructorLimit.max}
        overLimit={instructorLimit.overLimit}
        strictlyOver={instructorStrictly.overLimit}
      />
      <PageHeader
        title="Instructors"
        description={`Manage ${instructors.length} instructors at your institute`}
        icon={UserCog}
      />

      {/* Toolbar */}
      <div className="inst-toolbar">
        <AppButton className="inst-add-btn" onClick={() => navigate('/instructors/add')} disabled={instructorLimit.overLimit} title={instructorLimit.overLimit ? `Plan limit reached (${instructorLimit.used}/${instructorLimit.max})` : ''}>
          <Plus size={16} />
          <span className="inst-add-btn-text">Add Instructor</span>
        </AppButton>

        <div className="inst-search">
          <Search size={16} className="inst-search__icon" />
          <input
            type="text"
            className="inst-search__input"
            placeholder="Search instructors..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>

        <div className="inst-toolbar-actions">
          <ViewToggle view={view} onViewChange={setView} />

          {/* Desktop inline filter */}
          <select
            className="inst-filter-select d-none d-md-block"
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>

          {/* Mobile filter button */}
          <button className="inst-filter-btn d-md-none" onClick={() => setShowFilterModal(true)}>
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && <span className="inst-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Status">
          <select
            className="form-select"
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </FilterGroup>
      </FilterModal>

      {/* Content */}
      {loading ? (
        <div className="inst-loading">
          <div className="spinner-border spinner-border-sm" role="status" />
          <span>Loading instructors...</span>
        </div>
      ) : filteredInstructors.length === 0 ? (
        <EmptyState
          icon={<UserCog size={40} />}
          title="No instructors found"
          description="Try adjusting your search criteria or add a new instructor"
        />
      ) : view === 'grid' ? (
        <div className="inst-grid">
          {filteredInstructors.map(instructor => (
            <InstructorCard
              key={instructor._id}
              instructor={instructor}
              onView={inst => navigate(`/instructors/${inst._id}`)}
              onEdit={inst => navigate(`/instructors/edit/${inst._id}`)}
              onDelete={handleDelete}
              resendWelcomeEmail={resendWelcomeEmail}
            />
          ))}
        </div>
      ) : (
        <AppCard className="inst-table-card">
          <AppCardContent className="p-0">
            <div className="inst-table-wrapper">
              <table className="inst-table">
                <thead>
                  <tr>
                    <th>Instructor</th>
                    <th>Specialization</th>
                    <th>Experience</th>
                    <th className="text-center">Students</th>
                    <th className="text-center">Rating</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstructors.map(instructor => {
                    const courses = instructor.subject ? instructor.subject.split(',').map(c => c.trim()) : [];
                    return (
                      <tr key={instructor._id}>
                        <td>
                          <div className="inst-cell-user">
                            <UserAvatar name={instructor.name} size="sm" />
                            <div>
                              <span
                                className="inst-cell-user__name"
                                onClick={() => navigate(`/instructors/${instructor._id}`)}
                              >
                                {instructor.name}
                              </span>
                              <span className="inst-cell-user__email">{instructor.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{courses.join(', ') || '-'}</td>
                        <td>{instructor.experience ? `${instructor.experience} yrs` : '-'}</td>
                        <td className="text-center">{instructor.students || '-'}</td>
                        <td className="text-center">
                          <span className="inst-rating">
                            <Star size={14} fill="currentColor" />
                            {instructor.rating || '—'}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={instructor.status?.toLowerCase() || 'active'}>
                            {instructor.status || 'Active'}
                          </StatusBadge>
                        </td>
                        <td className="text-end inst-cell-actions">
                          <button className="inst-action-btn" title="View" onClick={() => navigate(`/instructors/${instructor._id}`)}>
                            <Eye size={15} />
                          </button>
                          <button className="inst-action-btn" title="Edit" onClick={() => navigate(`/instructors/edit/${instructor._id}`)}>
                            <Edit size={15} />
                          </button>
                          <button className="inst-action-btn" title="Resend Email" onClick={() => resendWelcomeEmail(instructor)}>
                            <Send size={15} />
                          </button>
                          <button className="inst-action-btn inst-action-btn--danger" title="Delete" onClick={() => handleDelete(instructor)}>
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      <CommonToaster
        show={toast?.show}
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        onConfirm={() => confirmAction?.onConfirm()}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.confirmText || 'Confirm'}
        variant={confirmAction?.variant || 'danger'}
      />
    </div>
  );
};

export default InstructorsPage;
